import { formatDate, t } from "@hakili/i18n";
import { Button, Card, Field, GuardedButton } from "@hakili/ui";
import { type ReactElement, useEffect, useState } from "react";

import type { DealerBankAccount, DoorResult } from "../session/useSession.js";
import { ReadFailure } from "./read-failure.js";

const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [hole, value]) => text.split(`{${hole}}`).join(value), t(key));

/**
 * `round T-2`, `M0 (c)` and `M5`: **the dealership's own bank account, entered on its own
 * screen** , the beneficiary of the 90 % (WF-BNK-16 step 5's first transfer). Read and written by
 * `GET`/`PUT /api/v1/me/dealership/bank-account` under `gererEquipeConcession` (the responsable's);
 * every save is a new version, the ordre reads the latest. A collaborateur reads it and cannot
 * write, greyed with the reason (I19). Nothing here is Hakili's hand.
 */
export function BankAccountBlock(props: {
  readonly readBankAccount: () => Promise<DoorResult<DealerBankAccount | null>>;
  readonly writeBankAccount: (input: {
    bankName: string;
    holderName: string;
    accountNumber: string;
  }) => Promise<DoorResult<DealerBankAccount>>;
  readonly canWrite: boolean;
}): ReactElement {
  const { readBankAccount } = props;
  const [account, setAccount] = useState<DealerBankAccount | null | undefined>(undefined);
  const [editing, setEditing] = useState(false);
  const [bankName, setBankName] = useState("");
  const [holderName, setHolderName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  /** `A4` part 2: the door's message when the READ failed; while it stands, no account is said and no form is offered. */
  const [echec, setEchec] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    void readBankAccount().then((answer) => {
      if (!alive) return;
      if (answer.ok) {
        setAccount(answer.value);
        setEchec(null);
      } else {
        setEchec(answer.message);
      }
    });
    return () => {
      alive = false;
    };
  }, [readBankAccount, nonce]);

  const complete = bankName.trim() !== "" && holderName.trim() !== "" && accountNumber.trim() !== "";
  return (
    <Card>
      <div data-testid="profil-compte">
        <h2 className="text-base font-semibold text-gray-900">{t("dealer.profil.compteTitre")}</h2>
        <p className="mt-1 text-sm text-gray-500">{t("dealer.profil.compteMots")}</p>
        {echec !== null ? (
          <div className="mt-3">
            <ReadFailure
              what="dealer.lecture.coordonnees"
              message={echec}
              onRetry={() => {
                setNonce((one) => one + 1);
              }}
              testId="profil-compte-echec"
              retryTestId="profil-compte-echec-reessayer"
            />
          </div>
        ) : (
          <>
            {account === undefined ? null : account === null ? (
              <p className="mt-3 text-sm text-amber-700" data-testid="profil-compte-absent">
                {t("dealer.profil.compteAbsent")}
              </p>
            ) : (
              <div className="mt-3 text-sm text-gray-700" data-testid="profil-compte-lecture">
                <p>
                  {t("dealer.profil.banque")} :{" "}
                  <span className="font-medium text-gray-900">{account.bankName}</span>
                </p>
                <p>
                  {t("dealer.profil.titulaire")} :{" "}
                  <span className="font-medium text-gray-900">{account.holderName}</span>
                </p>
                <p>
                  {t("dealer.profil.numeroCompte")} :{" "}
                  <span className="font-mono font-medium text-gray-900" data-testid="profil-compte-numero">
                    {account.accountNumber}
                  </span>
                </p>
                <p className="mt-1 text-xs text-gray-400" data-testid="profil-compte-version">
                  {fill("dealer.profil.compteVersion", {
                    version: String(account.version),
                    date: formatDate(new Date(account.enteredAt)),
                  })}
                </p>
              </div>
            )}
            {editing ? (
              <div className="mt-3 grid gap-3" data-testid="profil-compte-formulaire">
                <Field label={t("dealer.profil.banque")} htmlFor="compte-banque">
                  <input
                    id="compte-banque"
                    value={bankName}
                    onChange={(event) => {
                      setBankName(event.target.value);
                    }}
                    data-testid="profil-compte-banque"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>
                <Field label={t("dealer.profil.titulaire")} htmlFor="compte-titulaire">
                  <input
                    id="compte-titulaire"
                    value={holderName}
                    onChange={(event) => {
                      setHolderName(event.target.value);
                    }}
                    data-testid="profil-compte-titulaire"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>
                <Field label={t("dealer.profil.numeroCompte")} htmlFor="compte-numero">
                  <input
                    id="compte-numero"
                    value={accountNumber}
                    onChange={(event) => {
                      setAccountNumber(event.target.value);
                    }}
                    data-testid="profil-compte-numero-saisie"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>
                <div className="flex gap-2">
                  {complete ? (
                    <Button
                      variant="primary"
                      data-testid="profil-compte-enregistrer"
                      onClick={() => {
                        if (busy) return;
                        setBusy(true);
                        setRefusal(null);
                        void props
                          .writeBankAccount({
                            bankName: bankName.trim(),
                            holderName: holderName.trim(),
                            accountNumber: accountNumber.trim(),
                          })
                          .then((answer) => {
                            setBusy(false);
                            if (answer.ok) {
                              setAccount(answer.value);
                              setEditing(false);
                            } else {
                              setRefusal(answer.message);
                            }
                          });
                      }}
                    >
                      {t("dealer.profil.compteEnregistrer")}
                    </Button>
                  ) : (
                    <GuardedButton
                      disabled
                      reason={t("dealer.profil.compteEnregistrerMotif")}
                      variant="primary"
                      testId="profil-compte-enregistrer"
                    >
                      {t("dealer.profil.compteEnregistrer")}
                    </GuardedButton>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setEditing(false);
                      setRefusal(null);
                    }}
                    data-testid="profil-compte-annuler"
                  >
                    {t("common.action.annuler")}
                  </Button>
                </div>
                {refusal === null ? null : (
                  <p role="alert" className="text-sm text-rose-600" data-testid="profil-compte-refus">
                    {refusal}
                  </p>
                )}
              </div>
            ) : props.canWrite ? (
              <div className="mt-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setBankName(account?.bankName ?? "");
                    setHolderName(account?.holderName ?? "");
                    setAccountNumber(account?.accountNumber ?? "");
                    setEditing(true);
                  }}
                  data-testid="profil-compte-modifier"
                >
                  {t("dealer.profil.compteEnregistrer")}
                </Button>
              </div>
            ) : (
              <div className="mt-3">
                <GuardedButton
                  disabled
                  reason={t("dealer.profil.modifierRefus")}
                  variant="secondary"
                  testId="profil-compte-modifier"
                >
                  {t("dealer.profil.compteEnregistrer")}
                </GuardedButton>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
